import {
  useEffect,
  useDeferredValue,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import type { Membership, Tenant } from '../domain-types';
import { profileViewTabs, type ProfileViewerCapability } from '../profile-view-access';

import './administration-directory.css';

const directoryPageSize = 25;

type DirectoryMembership = Membership & {
  organisationName: string;
  organisationSlug?: string;
  organisationStatus?: Tenant['status'];
};

type PlatformPersonSummary = DirectoryMembership & {
  displayName: string;
};

export type PlatformPersonDetail = {
  userId: string;
  email?: string;
  isPlatformAdmin: boolean;
  mfaEnabled: boolean | null;
  profile?: {
    fullName: string;
    firstName?: string;
    familyName?: string;
    phone?: string;
    bio?: string;
    pictureUrl?: string;
    timezone?: string;
    lastSeenAt?: string;
  };
  memberships: DirectoryMembership[];
};

type PeopleDirectoryResponse = {
  items: PlatformPersonSummary[];
  nextCursor?: string;
};

type PeopleDirectoryRebuildResponse = {
  processed: number;
  nextCursor?: string;
};

function CursorPagination({
  page,
  hasPreviousPage,
  hasNextPage,
  onPreviousPage,
  onNextPage,
}: {
  page: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onPreviousPage: () => void;
  onNextPage: () => void;
}) {
  if (!hasPreviousPage && !hasNextPage) return null;
  return (
    <nav aria-label="people pagination" className="directory-pagination">
      <button disabled={!hasPreviousPage} type="button" onClick={onPreviousPage}>
        Previous
      </button>
      <span>Page {page}</span>
      <button disabled={!hasNextPage} type="button" onClick={onNextPage}>
        Next
      </button>
    </nav>
  );
}

type DirectoryPage = 'admin-organisations' | 'admin-people';

function directoryDetailId(page: DirectoryPage): string {
  const [currentPage, encodedId] = window.location.hash.slice(1).split('/');
  if (currentPage !== page || !encodedId) return '';
  try {
    return decodeURIComponent(encodedId);
  } catch {
    return '';
  }
}

function openDirectoryLocation(page: DirectoryPage, id?: string) {
  window.location.hash = id ? `${page}/${encodeURIComponent(id)}` : page;
}

function useDirectoryDetailId(page: DirectoryPage): string {
  const [id, setId] = useState(() => directoryDetailId(page));

  useEffect(() => {
    const sync = () => setId(directoryDetailId(page));
    sync();
    window.addEventListener('hashchange', sync);
    return () => window.removeEventListener('hashchange', sync);
  }, [page]);

  return id;
}

const roleNames: Record<string, string> = {
  standard_user: 'Standard User',
  team_leader: 'Team Leader',
  manager: 'Team Manager',
  tenant_admin: 'Organisation Administrator',
  viewer: 'Standard User',
  auditor: 'Standard User',
  quality_manager: 'Team Manager',
};

function roleLabel(role: string): string {
  return roleNames[role] ?? role.replaceAll('_', ' ');
}

function dateLabel(value: string | undefined, fallback = 'Not available'): string {
  if (!value) return fallback;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? fallback : date.toLocaleString();
}

function initials(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || 'QV'
  );
}

function Pagination({
  page,
  pageCount,
  total,
  label,
  onPageChange,
}: {
  page: number;
  pageCount: number;
  total: number;
  label: string;
  onPageChange: (page: number) => void;
}) {
  if (total <= directoryPageSize) return null;
  return (
    <nav aria-label={`${label} pagination`} className="directory-pagination">
      <button disabled={page <= 1} type="button" onClick={() => onPageChange(page - 1)}>
        Previous
      </button>
      <span>
        Page {page} of {pageCount} · {total} {label}
      </span>
      <button disabled={page >= pageCount} type="button" onClick={() => onPageChange(page + 1)}>
        Next
      </button>
    </nav>
  );
}

function DetailTabs({
  activeTab,
  tabs,
  onChange,
  idPrefix,
}: {
  activeTab: string;
  tabs: Array<{ id: string; label: string }>;
  onChange: (tab: string) => void;
  idPrefix: string;
}) {
  const tabListRef = useRef<HTMLDivElement>(null);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();

    const tabButtons = [
      ...(tabListRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]') ?? []),
    ];
    if (tabButtons.length === 0) return;

    const currentIndex = tabButtons.findIndex((tab) => tab === document.activeElement);
    const nextIndex =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? tabButtons.length - 1
          : (currentIndex + (event.key === 'ArrowRight' ? 1 : -1) + tabButtons.length) %
            tabButtons.length;
    const nextTab = tabButtons[nextIndex];
    const nextValue = nextTab?.dataset.tab;
    if (!nextTab || !nextValue) return;
    onChange(nextValue);
    nextTab.focus();
  };

  return (
    <div
      aria-label="Detail sections"
      className="directory-tabs"
      ref={tabListRef}
      role="tablist"
      onKeyDown={handleKeyDown}
    >
      {tabs.map((tab) => (
        <button
          aria-controls={`${idPrefix}-${tab.id}-panel`}
          aria-selected={activeTab === tab.id}
          className={activeTab === tab.id ? 'active' : undefined}
          data-tab={tab.id}
          id={`${idPrefix}-${tab.id}-tab`}
          key={tab.id}
          role="tab"
          tabIndex={activeTab === tab.id ? 0 : -1}
          type="button"
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function PersonProfileView({
  person,
  viewerCapability,
  onBack,
  onOpenOrganisation,
  onRevealSensitive,
  sensitiveDetailsRevealed = false,
  revealingSensitive = false,
  actions,
}: {
  person: PlatformPersonDetail;
  viewerCapability: ProfileViewerCapability;
  onBack?: () => void;
  onOpenOrganisation?: (tenantId: string) => void;
  onRevealSensitive?: () => void;
  sensitiveDetailsRevealed?: boolean;
  revealingSensitive?: boolean;
  actions?: ReactNode;
}) {
  const [activeTab, setActiveTab] = useState('profile');
  const displayName = person.profile?.fullName || person.email || person.userId;
  const tabs = profileViewTabs(viewerCapability).map((id) => ({
    id,
    label:
      id === 'profile'
        ? 'Profile'
        : id === 'access'
          ? 'Organisation access'
          : id === 'security'
            ? 'Security'
            : 'Platform',
  }));

  useEffect(() => {
    if (!tabs.some((tab) => tab.id === activeTab)) setActiveTab('profile');
  }, [activeTab, tabs]);

  return (
    <section className="directory-detail" aria-labelledby="person-directory-title">
      <header className="directory-detail-header">
        <div className="directory-detail-heading">
          {onBack ? (
            <button className="directory-back" type="button" onClick={onBack}>
              ← Back to people
            </button>
          ) : null}
          <div className="directory-person-heading">
            {person.profile?.pictureUrl ? (
              <img alt="" src={person.profile.pictureUrl} />
            ) : (
              <span aria-hidden="true">{initials(displayName)}</span>
            )}
            <div>
              <p className="eyebrow">Person profile</p>
              <h1 id="person-directory-title">{displayName}</h1>
              <p>{person.email ?? 'No email address is available'}</p>
            </div>
          </div>
        </div>
        {actions}
      </header>

      <DetailTabs
        activeTab={activeTab}
        idPrefix="person-directory"
        tabs={tabs}
        onChange={setActiveTab}
      />

      <div
        aria-labelledby={`person-directory-${activeTab}-tab`}
        id={`person-directory-${activeTab}-panel`}
        role="tabpanel"
      >
        {activeTab === 'profile' ? (
          <div className="directory-card-grid">
            <article className="directory-card">
              <h2>Contact</h2>
              <dl className="directory-definition-list">
                <div>
                  <dt>Email</dt>
                  <dd>{person.email ?? 'Not available'}</dd>
                </div>
                {sensitiveDetailsRevealed ? (
                  <>
                    <div>
                      <dt>Phone</dt>
                      <dd>{person.profile?.phone || 'Not provided'}</dd>
                    </div>
                    <div>
                      <dt>Timezone</dt>
                      <dd>{person.profile?.timezone || 'Not provided'}</dd>
                    </div>
                  </>
                ) : null}
              </dl>
            </article>
            <article className="directory-card">
              {sensitiveDetailsRevealed ? (
                <>
                  <h2>Bio</h2>
                  <p>{person.profile?.bio || 'No bio has been provided.'}</p>
                </>
              ) : (
                <>
                  <h2>Additional personal details</h2>
                  <p>Phone, timezone, bio, profile image and activity information.</p>
                  {onRevealSensitive ? (
                    <button disabled={revealingSensitive} type="button" onClick={onRevealSensitive}>
                      {revealingSensitive ? 'Loading details…' : 'Show additional details'}
                    </button>
                  ) : null}
                </>
              )}
            </article>
          </div>
        ) : null}

        {activeTab === 'access' ? (
          <div className="directory-memberships">
            {person.memberships.map((membership) => (
              <article key={`${membership.tenantId}-${membership.userId}`}>
                <div>
                  {onOpenOrganisation ? (
                    <button
                      className="directory-name-link"
                      type="button"
                      onClick={() => onOpenOrganisation(membership.tenantId)}
                    >
                      {membership.organisationName}
                    </button>
                  ) : (
                    <strong>{membership.organisationName}</strong>
                  )}
                  <small>{membership.roles.map(roleLabel).join(', ')}</small>
                  {membership.jobTitle ? <small>{membership.jobTitle}</small> : null}
                  <small>Locations: {(membership.locationIds ?? []).join(', ') || 'None'}</small>
                  <small>Teams: {(membership.teamIds ?? []).join(', ') || 'None'}</small>
                  <small>Managers: {(membership.managerUserIds ?? []).join(', ') || 'None'}</small>
                </div>
                <span className={`directory-status directory-status-${membership.status}`}>
                  {membership.status}
                </span>
              </article>
            ))}
          </div>
        ) : null}

        {activeTab === 'security' ? (
          <div className="directory-card-grid">
            <article className="directory-card">
              <h2>Multi-factor authentication</h2>
              <p>
                {person.mfaEnabled === true
                  ? 'Authenticator-app MFA is enabled.'
                  : person.mfaEnabled === false
                    ? 'Authenticator-app MFA is not enabled.'
                    : 'MFA status is unavailable.'}
              </p>
            </article>
            {sensitiveDetailsRevealed ? (
              <article className="directory-card">
                <h2>Recent activity</h2>
                <p>Last app activity: {dateLabel(person.profile?.lastSeenAt)}</p>
              </article>
            ) : null}
          </div>
        ) : null}

        {activeTab === 'platform' ? (
          <article className="directory-card directory-platform-card">
            <h2>Super Administrator visibility</h2>
            <dl className="directory-definition-list">
              <div>
                <dt>User ID</dt>
                <dd className="directory-monospace">{person.userId}</dd>
              </div>
              <div>
                <dt>Platform administrator</dt>
                <dd>{person.isPlatformAdmin ? 'Yes' : 'No'}</dd>
              </div>
              <div>
                <dt>Organisation memberships</dt>
                <dd>{person.memberships.length}</dd>
              </div>
            </dl>
          </article>
        ) : null}
      </div>
    </section>
  );
}

export function PlatformPeopleDirectory({
  accessToken,
  apiUrl,
  tenants,
  fixedTenantId,
  embedded = false,
  onOpenOrganisation,
}: {
  accessToken: string;
  apiUrl: string;
  tenants: Tenant[];
  fixedTenantId?: string;
  embedded?: boolean;
  onOpenOrganisation?: (tenantId: string) => void;
}) {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [tenantFilter, setTenantFilter] = useState(fixedTenantId ?? '');
  const [page, setPage] = useState(1);
  const [cursors, setCursors] = useState<string[]>([]);
  const [response, setResponse] = useState<PeopleDirectoryResponse>({ items: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const selectedPersonId = useDirectoryDetailId('admin-people');
  const [selectedPerson, setSelectedPerson] = useState<PlatformPersonDetail>();
  const [personLoading, setPersonLoading] = useState(false);
  const [sensitiveDetailsRevealed, setSensitiveDetailsRevealed] = useState(false);
  const [revealingSensitive, setRevealingSensitive] = useState(false);
  const [directoryRevision, setDirectoryRevision] = useState(0);
  const [rebuildingDirectory, setRebuildingDirectory] = useState(false);
  const [rebuildMessage, setRebuildMessage] = useState('');
  const [rebuildCursor, setRebuildCursor] = useState<string>();

  useEffect(() => {
    if (fixedTenantId) setTenantFilter(fixedTenantId);
  }, [fixedTenantId]);

  useEffect(() => {
    const controller = new AbortController();
    const parameters = new URLSearchParams();
    const cursor = cursors[page - 1];
    if (cursor) parameters.set('cursor', cursor);
    if (search) parameters.set('q', search);
    if (tenantFilter) parameters.set('tenantId', tenantFilter);
    setLoading(true);
    setError('');
    void fetch(`${apiUrl}/v1/platform/people?${parameters.toString()}`, {
      headers: { authorization: `Bearer ${accessToken}` },
      signal: controller.signal,
    })
      .then(async (result) => {
        if (!result.ok) throw new Error(`HTTP ${result.status}`);
        setResponse((await result.json()) as PeopleDirectoryResponse);
      })
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === 'AbortError')) {
          setError('People could not be loaded.');
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [accessToken, apiUrl, cursors, directoryRevision, page, search, tenantFilter]);

  useEffect(() => {
    if (!selectedPersonId) {
      setSelectedPerson(undefined);
      setSensitiveDetailsRevealed(false);
      return;
    }
    const controller = new AbortController();
    setPersonLoading(true);
    setError('');
    void fetch(`${apiUrl}/v1/platform/people/${encodeURIComponent(selectedPersonId)}`, {
      headers: { authorization: `Bearer ${accessToken}` },
      signal: controller.signal,
    })
      .then(async (result) => {
        if (!result.ok) throw new Error(`HTTP ${result.status}`);
        setSelectedPerson((await result.json()) as PlatformPersonDetail);
      })
      .catch((reason: unknown) => {
        if (!(reason instanceof DOMException && reason.name === 'AbortError')) {
          setError('The person profile could not be loaded.');
        }
      })
      .finally(() => setPersonLoading(false));
    return () => controller.abort();
  }, [accessToken, apiUrl, selectedPersonId]);

  const revealSensitiveDetails = () => {
    if (!selectedPersonId || revealingSensitive) return;
    setRevealingSensitive(true);
    setError('');
    void fetch(
      `${apiUrl}/v1/platform/people/${encodeURIComponent(selectedPersonId)}?includeSensitive=true`,
      { headers: { authorization: `Bearer ${accessToken}` } },
    )
      .then(async (result) => {
        if (!result.ok) throw new Error(`HTTP ${result.status}`);
        setSelectedPerson((await result.json()) as PlatformPersonDetail);
        setSensitiveDetailsRevealed(true);
      })
      .catch(() => setError('Additional details could not be loaded.'))
      .finally(() => setRevealingSensitive(false));
  };

  const applySearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setPage(1);
    setCursors([]);
    setSearch(searchInput.trim());
  };

  const changeTenantFilter = (tenantId: string) => {
    setTenantFilter(tenantId);
    setPage(1);
    setCursors([]);
  };

  const nextPage = () => {
    if (!response.nextCursor) return;
    setCursors((current) => {
      const next = [...current];
      next[page] = response.nextCursor!;
      return next;
    });
    setPage((current) => current + 1);
  };

  const rebuildDirectory = async () => {
    setRebuildingDirectory(true);
    setError('');
    setRebuildMessage('');
    try {
      const parameters = new URLSearchParams();
      if (rebuildCursor) parameters.set('cursor', rebuildCursor);
      const result = await fetch(
        `${apiUrl}/v1/platform/people/rebuild-directory?${parameters.toString()}`,
        { headers: { authorization: `Bearer ${accessToken}` }, method: 'POST' },
      );
      if (!result.ok) throw new Error(`HTTP ${result.status}`);
      const body = (await result.json()) as PeopleDirectoryRebuildResponse;
      setRebuildCursor(body.nextCursor);
      setPage(1);
      setCursors([]);
      setDirectoryRevision((current) => current + 1);
      setRebuildMessage(
        body.nextCursor
          ? `${body.processed} membership records indexed. Select Update directory to continue.`
          : `Directory updated: ${body.processed} membership records indexed in the final batch.`,
      );
    } catch {
      setError('The people directory could not be updated.');
    } finally {
      setRebuildingDirectory(false);
    }
  };

  if (selectedPersonId) {
    if (personLoading || !selectedPerson) {
      return <p className="directory-loading">Loading person profile...</p>;
    }
    return (
      <PersonProfileView
        person={selectedPerson}
        viewerCapability="super_admin"
        onBack={() => openDirectoryLocation('admin-people')}
        onRevealSensitive={revealSensitiveDetails}
        sensitiveDetailsRevealed={sensitiveDetailsRevealed}
        revealingSensitive={revealingSensitive}
        onOpenOrganisation={
          onOpenOrganisation ??
          ((tenantId) => openDirectoryLocation('admin-organisations', tenantId))
        }
      />
    );
  }

  return (
    <section className={embedded ? 'directory-page directory-page-embedded' : 'directory-page'}>
      <header className="directory-page-header">
        <div>
          {!embedded ? <p className="eyebrow">Super Administrator</p> : null}
          <h1>{fixedTenantId ? 'People' : 'People directory'}</h1>
          <p>
            Browse people
            {fixedTenantId ? ' linked to this organisation' : ' across all organisations'}.
          </p>
        </div>
        <form className="directory-toolbar" role="search" onSubmit={applySearch}>
          {!fixedTenantId ? (
            <label>
              <span className="directory-search-label">Filter by organisation</span>
              <select
                value={tenantFilter}
                onChange={(event) => {
                  changeTenantFilter(event.target.value);
                }}
              >
                <option value="">All organisations</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
          <label className="directory-search">
            <span className="directory-search-label">Search people</span>
            <input
              type="search"
              placeholder="Search people"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <button type="submit">Search</button>
          {!embedded && !fixedTenantId ? (
            <button
              disabled={rebuildingDirectory}
              type="button"
              onClick={() => void rebuildDirectory()}
            >
              {rebuildingDirectory ? 'Updating directory…' : 'Update directory'}
            </button>
          ) : null}
        </form>
      </header>

      {error ? (
        <p className="auth-error" role="alert">
          {error}
        </p>
      ) : null}
      {rebuildMessage ? <p className="directory-empty">{rebuildMessage}</p> : null}
      {loading ? <p className="directory-loading">Loading people...</p> : null}
      {!loading && response.items.length === 0 ? (
        <p className="directory-empty">No people match the current filters.</p>
      ) : null}
      {!loading && response.items.length > 0 ? (
        <div className="directory-table-scroll">
          <table className="directory-table">
            <thead>
              <tr>
                <th scope="col">Person</th>
                {!fixedTenantId ? <th scope="col">Organisation</th> : null}
                <th scope="col">Role</th>
                <th scope="col">Status</th>
                <th scope="col">Joined</th>
              </tr>
            </thead>
            <tbody>
              {response.items.map((person) => (
                <tr key={`${person.tenantId}-${person.userId}`}>
                  <td>
                    <button
                      className="directory-name-link"
                      type="button"
                      onClick={() => openDirectoryLocation('admin-people', person.userId)}
                    >
                      {person.displayName}
                    </button>
                    <small>{person.email}</small>
                  </td>
                  {!fixedTenantId ? <td>{person.organisationName}</td> : null}
                  <td>{person.roles.map(roleLabel).join(', ')}</td>
                  <td>
                    <span className={`directory-status directory-status-${person.status}`}>
                      {person.status}
                    </span>
                  </td>
                  <td>{dateLabel(person.createdAt, 'Not recorded')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
      <CursorPagination
        page={page}
        hasPreviousPage={page > 1}
        hasNextPage={Boolean(response.nextCursor)}
        onPreviousPage={() => setPage((current) => Math.max(1, current - 1))}
        onNextPage={nextPage}
      />
    </section>
  );
}

export function OrganisationDirectory({
  accessToken,
  apiUrl,
  tenants,
  busy,
  giftDurationDays,
  giftReason,
  onGiftDurationDaysChange,
  onGiftReasonChange,
  onUpdateTenantLifecycle,
  onGiftSubscription,
  onRequestTenantDeletion,
  onSelectTenant,
}: {
  accessToken: string;
  apiUrl: string;
  tenants: Tenant[];
  busy: boolean;
  giftDurationDays: number;
  giftReason: string;
  onGiftDurationDaysChange: (value: number) => void;
  onGiftReasonChange: (value: string) => void;
  onUpdateTenantLifecycle: (tenantId: string, update: Record<string, unknown>) => Promise<void>;
  onGiftSubscription: (tenantId: string) => Promise<void>;
  onRequestTenantDeletion: (tenant: Tenant) => Promise<void>;
  onSelectTenant?: (tenantId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [remoteTenants, setRemoteTenants] = useState(tenants);
  const [total, setTotal] = useState(tenants.length);
  const [totalPages, setTotalPages] = useState(1);
  const selectedTenantId = useDirectoryDetailId('admin-organisations');
  const [activeTab, setActiveTab] = useState('overview');
  useEffect(() => {
    let active = true;
    const parameters = new URLSearchParams({ page: String(page) });
    if (deferredSearch.trim()) parameters.set('search', deferredSearch.trim());
    if (status) parameters.set('status', status);
    void fetch(`${apiUrl}/v1/platform/tenants?${parameters}`, {
      headers: { authorization: `Bearer ${accessToken}` },
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return (await response.json()) as {
          items: Tenant[];
          total: number;
          totalPages: number;
        };
      })
      .then((response) => {
        if (!active) return;
        setRemoteTenants(response.items);
        setTotal(response.total);
        setTotalPages(response.totalPages);
      })
      .catch(() => active && setRemoteTenants([]));
    return () => {
      active = false;
    };
  }, [accessToken, apiUrl, deferredSearch, page, status, tenants]);
  const selectedTenant =
    remoteTenants.find((tenant) => tenant.id === selectedTenantId) ??
    tenants.find((tenant) => tenant.id === selectedTenantId);

  const openTenant = (tenantId: string) => {
    openDirectoryLocation('admin-organisations', tenantId);
    setActiveTab('overview');
    onSelectTenant?.(tenantId);
  };

  if (selectedTenant) {
    const tabs = [
      { id: 'overview', label: 'Overview' },
      { id: 'people', label: 'People' },
      { id: 'structure', label: 'Structure' },
      { id: 'super-admin', label: 'Super Administrator' },
    ];
    return (
      <section className="directory-detail" aria-labelledby="organisation-directory-title">
        <header className="directory-detail-header">
          <div className="directory-detail-heading">
            <button
              className="directory-back"
              type="button"
              onClick={() => openDirectoryLocation('admin-organisations')}
            >
              ← Back to organisations
            </button>
            <p className="eyebrow">Organisation</p>
            <h1 id="organisation-directory-title">{selectedTenant.name}</h1>
            <p>{selectedTenant.slug}</p>
          </div>
          <span className={`directory-status directory-status-${selectedTenant.status}`}>
            {selectedTenant.status}
          </span>
        </header>

        <DetailTabs
          activeTab={activeTab}
          idPrefix="organisation-directory"
          tabs={tabs}
          onChange={setActiveTab}
        />

        <div
          aria-labelledby={`organisation-directory-${activeTab}-tab`}
          id={`organisation-directory-${activeTab}-panel`}
          role="tabpanel"
        >
          {activeTab === 'overview' ? (
            <div className="directory-card-grid">
              <article className="directory-card">
                <h2>Organisation details</h2>
                <dl className="directory-definition-list">
                  <div>
                    <dt>Provider code</dt>
                    <dd>{selectedTenant.tgaProviderCode ?? 'Not linked'}</dd>
                  </div>
                  <div>
                    <dt>Provider verification</dt>
                    <dd>{selectedTenant.providerVerificationStatus ?? 'Pending'}</dd>
                  </div>
                  <div>
                    <dt>MFA required</dt>
                    <dd>{selectedTenant.mfaRequired ? 'Yes' : 'No'}</dd>
                  </div>
                </dl>
              </article>
              <article className="directory-card">
                <h2>Lifecycle and access</h2>
                <dl className="directory-definition-list">
                  <div>
                    <dt>Onboarding</dt>
                    <dd>{selectedTenant.onboardingStatus ?? 'Not started'}</dd>
                  </div>
                  <div>
                    <dt>Offboarding</dt>
                    <dd>{selectedTenant.offboardingStatus ?? 'None'}</dd>
                  </div>
                  <div>
                    <dt>Access until</dt>
                    <dd>{dateLabel(selectedTenant.subscriptionAccessUntil, 'Not granted')}</dd>
                  </div>
                  <div>
                    <dt>Deletion retention</dt>
                    <dd>
                      {selectedTenant.deletionRetentionState
                        ? `${selectedTenant.deletionRetentionState.replaceAll('_', ' ')}${
                            selectedTenant.deletionRetentionDueAt
                              ? ` · due ${dateLabel(selectedTenant.deletionRetentionDueAt, '')}`
                              : ''
                          }`
                        : 'Not scheduled'}
                    </dd>
                  </div>
                </dl>
              </article>
            </div>
          ) : null}

          {activeTab === 'people' ? (
            <PlatformPeopleDirectory
              accessToken={accessToken}
              apiUrl={apiUrl}
              embedded
              fixedTenantId={selectedTenant.id}
              tenants={tenants}
              onOpenOrganisation={openTenant}
            />
          ) : null}

          {activeTab === 'structure' ? (
            <div className="directory-card-grid">
              <article className="directory-card">
                <h2>Locations</h2>
                {selectedTenant.locations?.length ? (
                  <ul>
                    {selectedTenant.locations.map((location) => (
                      <li key={location.id}>
                        <strong>{location.name}</strong>
                        <span>
                          {[
                            ...location.addressLines,
                            location.suburb,
                            location.state,
                            location.postcode,
                          ]
                            .filter(Boolean)
                            .join(', ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No locations have been recorded.</p>
                )}
              </article>
              <article className="directory-card">
                <h2>Teams</h2>
                {selectedTenant.teams?.length ? (
                  <ul>
                    {selectedTenant.teams.map((team) => (
                      <li key={team.id}>
                        <strong>{team.name}</strong>
                        <span>{team.locationIds.length} linked locations</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p>No teams have been recorded.</p>
                )}
              </article>
            </div>
          ) : null}

          {activeTab === 'super-admin' ? (
            <div className="directory-card-grid">
              <article className="directory-card">
                <h2>Lifecycle controls</h2>
                <p>These controls are only visible to a Super Administrator.</p>
                <div className="directory-action-row">
                  {selectedTenant.status === 'onboarding' ? (
                    <button
                      disabled={busy}
                      type="button"
                      onClick={() =>
                        void onUpdateTenantLifecycle(selectedTenant.id, {
                          status: 'active',
                          onboardingStatus: 'complete',
                          onboardingChecklist: {
                            organisationDetailsConfirmed: true,
                            primaryAdministratorInvited: true,
                            dataRegionConfirmed: true,
                          },
                        })
                      }
                    >
                      Complete onboarding
                    </button>
                  ) : selectedTenant.status === 'active' ? (
                    <button
                      disabled={busy}
                      type="button"
                      onClick={() =>
                        void onUpdateTenantLifecycle(selectedTenant.id, { status: 'suspended' })
                      }
                    >
                      Suspend access
                    </button>
                  ) : selectedTenant.status === 'suspended' ? (
                    <button
                      disabled={busy}
                      type="button"
                      onClick={() =>
                        void onUpdateTenantLifecycle(selectedTenant.id, { status: 'active' })
                      }
                    >
                      Reactivate
                    </button>
                  ) : null}
                  {selectedTenant.offboardingStatus === 'requested' ? (
                    <button
                      disabled={busy}
                      type="button"
                      onClick={() =>
                        void onUpdateTenantLifecycle(selectedTenant.id, {
                          offboardingStatus: 'export_ready',
                        })
                      }
                    >
                      Mark export ready
                    </button>
                  ) : null}
                  {selectedTenant.offboardingStatus === 'export_ready' &&
                  selectedTenant.status !== 'archived' ? (
                    <button
                      disabled={busy}
                      type="button"
                      onClick={() =>
                        void onUpdateTenantLifecycle(selectedTenant.id, { status: 'archived' })
                      }
                    >
                      Archive organisation
                    </button>
                  ) : null}
                  {selectedTenant.status === 'archived' &&
                  selectedTenant.offboardingStatus === 'export_ready' &&
                  !selectedTenant.deletionRequestedAt ? (
                    <button
                      disabled={busy}
                      type="button"
                      onClick={() => void onRequestTenantDeletion(selectedTenant)}
                    >
                      Schedule controlled deletion
                    </button>
                  ) : null}
                  {selectedTenant.deletionRequestedAt ? (
                    <p>Deletion is scheduled and held pending the approved retention process.</p>
                  ) : null}
                </div>
              </article>
              <article className="directory-card">
                <h2>Complimentary access</h2>
                <label>
                  Duration (days)
                  <input
                    min={1}
                    max={1095}
                    type="number"
                    value={giftDurationDays}
                    onChange={(event) => onGiftDurationDaysChange(Number(event.target.value))}
                  />
                </label>
                <label>
                  Reason
                  <textarea
                    maxLength={2000}
                    minLength={10}
                    rows={3}
                    value={giftReason}
                    onChange={(event) => onGiftReasonChange(event.target.value)}
                  />
                </label>
                <button
                  disabled={
                    busy ||
                    selectedTenant.status === 'archived' ||
                    giftDurationDays < 1 ||
                    giftDurationDays > 1095 ||
                    giftReason.trim().length < 10
                  }
                  type="button"
                  onClick={() => void onGiftSubscription(selectedTenant.id)}
                >
                  Gift {giftDurationDays} days
                </button>
              </article>
            </div>
          ) : null}
        </div>
      </section>
    );
  }

  return (
    <section className="directory-page" aria-labelledby="organisation-list-title">
      <header className="directory-page-header">
        <div>
          <p className="eyebrow">Super Administrator</p>
          <h1 id="organisation-list-title">Organisations</h1>
          <p>{total} organisations</p>
        </div>
        <label className="directory-search">
          <span className="directory-search-label">Search organisations</span>
          <input
            type="search"
            placeholder="Search organisations"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </label>
        <label className="directory-search">
          <span className="directory-search-label">Status</span>
          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All statuses</option>
            <option value="onboarding">Onboarding</option>
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
            <option value="archived">Archived</option>
          </select>
        </label>
      </header>
      {remoteTenants.length ? (
        <div className="directory-table-scroll">
          <table className="directory-table">
            <thead>
              <tr>
                <th scope="col">Organisation</th>
                <th scope="col">Status</th>
                <th scope="col">Onboarding</th>
                <th scope="col">Locations</th>
                <th scope="col">Provider</th>
                <th scope="col">Access until</th>
                <th scope="col">Last updated</th>
              </tr>
            </thead>
            <tbody>
              {remoteTenants.map((tenant) => (
                <tr key={tenant.id}>
                  <td>
                    <button
                      className="directory-name-link"
                      type="button"
                      onClick={() => openTenant(tenant.id)}
                    >
                      {tenant.name}
                    </button>
                    <small>{tenant.slug}</small>
                  </td>
                  <td>
                    <span className={`directory-status directory-status-${tenant.status}`}>
                      {tenant.status}
                    </span>
                  </td>
                  <td>{tenant.onboardingStatus ?? 'Not started'}</td>
                  <td>{tenant.locations?.length ?? 0}</td>
                  <td>{tenant.tgaProviderCode ?? 'Not linked'}</td>
                  <td>{dateLabel(tenant.subscriptionAccessUntil, 'Not granted')}</td>
                  <td>{dateLabel(tenant.updatedAt, 'Not recorded')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="directory-empty">No organisations match this search.</p>
      )}
      <Pagination
        label="organisations"
        page={page}
        pageCount={totalPages}
        total={total}
        onPageChange={setPage}
      />
    </section>
  );
}
